import { FC, useMemo } from 'react';

import {
  Background,
  DefaultDailyBonus,
  FontAwesomeIcon,
  PostPurchase,
  ProductData
} from '@appcharge/shared-ui';

import useOffersUI from '../../../../api/useOffersUI';
import useTheme from '../../../../api/useTheme';
import { cn } from '../../../../components/lib/utils';
import Typography from '../../../../components/Typography/Typography';

import '../../../../common/styles.scss';

type PreviewSectionProps = {
  publisherId?: string;
  currentOfferUI: string;
  isDailyBonus: boolean;
  currentPrice: number;
  products: any;
  productsList: ProductData[];
};

const PreviewSection: FC<PreviewSectionProps> = ({
  publisherId,
  currentOfferUI,
  isDailyBonus,
  currentPrice,
  products,
  productsList
}) => {
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);
  const { getOfferUI } = useOffersUI(publisherId, currentOfferUI);

  const previewProducts = useMemo<ProductData[]>(() => {
    if (!products || productsList.length === 0) return [];

    return productsList.flatMap(({ productId, quantity, quantityDisplay }) => {
      const selectedProduct = products!.result.find(
        (product: ProductData) => product.productId === productId
      );
      if (!selectedProduct) return [];

      return [
        {
          ...selectedProduct,
          images: Object.fromEntries(
            selectedProduct.images.map(
              ({ type, url }: { type: string; url: string }) => [type, url]
            )
          ),
          quantity,
          quantityDisplay:
            quantityDisplay ||
            selectedProduct.quantityDisplay ||
            String(quantity)
        }
      ];
    });
  }, [products, productsList]);

  return (
    <div className="w-full h-full bg-zinc-100 px-16 py-10">
      <div className="mb-11 flex items-center justify-between">
        <Typography className="text-p-xl font-semibold">Preview</Typography>
        <div className="flex items-center bg-base-white rounded-lg shadow border border-base-sidebarBorder p-1.5">
          <FontAwesomeIcon
            icon="fa-solid fa-mobile"
            className={cn('text-indigo-300 text-[18px] w-8 h-8 cursor-pointer')}
          />
          <hr className="bg-base-border w-[1px] h-6 mx-1" />
          <FontAwesomeIcon
            icon="fa-regular fa-desktop"
            className={cn(
              'text-neutral-300 text-[18px] w-8 h-8 cursor-not-allowed'
            )}
          />
        </div>
      </div>
      <div className="flex justify-center">
        <div
          className="iphone-mock"
          id={isDailyBonus ? 'iphone-mock' : 'iphone-mock-post-purchase'}
        >
          <Background
            backgroundImageMobile={
              !generalTheme?.isLoading &&
              generalTheme.data?.backgroundImageMobile
            }
            backgroundImageDesktop={
              !generalTheme.isLoading &&
              generalTheme.data?.backgroundImageDesktop
            }
            width="100%"
            height="100%"
            position="absolute"
          />
          {isDailyBonus ? (
            <DefaultDailyBonus
              isPreview
              title={getOfferUI?.data?.specialOffer?.title || ''}
              titleColor={getOfferUI?.data?.specialOffer?.fontColor.colorOne}
              titleWeight={getOfferUI?.data?.specialOffer?.fontWeight}
              titleSize={getOfferUI?.data?.specialOffer?.fontSize.toString()}
              borderWidth={getOfferUI?.data?.borderWidth}
              borderColor={getOfferUI?.data?.borderColor}
              modalContainer={() => document.getElementById('iphone-mock')}
              backgroundImage={getOfferUI?.data?.backgroundImage}
              products={previewProducts}
              onCollect={async () => true}
              onClose={() => {}}
              onAnimationSuccessEndNoPreview={() => {}}
            />
          ) : (
            <PostPurchase
              isPreview
              title={getOfferUI?.data?.specialOffer?.title || ''}
              titleColor={getOfferUI?.data?.specialOffer?.fontColor.colorOne}
              titleWeight={getOfferUI?.data?.specialOffer?.fontWeight}
              titleSize={getOfferUI?.data?.specialOffer?.fontSize.toString()}
              borderWidth={getOfferUI?.data?.borderWidth}
              borderColor={getOfferUI?.data?.borderColor}
              modalContainer={() =>
                document.getElementById('iphone-mock-post-purchase')
              }
              backgroundImage={getOfferUI?.data?.backgroundImage}
              products={previewProducts}
              onActionBtnClicked={async () => true}
              onClose={() => {}}
              prices={{
                priceDisplay: `$${currentPrice}`,
                priceInUsdCents: currentPrice
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewSection;
